import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { DocumentUploadPanel } from '../components/DocumentUploadPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Search, Plus, Edit2, Trash2, BarChart3, ClipboardList, AlertTriangle, CheckCircle, Clock, XCircle, Bell, UserCheck, ArrowRightLeft, Eye, Gauge, RotateCcw, FileText, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  concluida: 'bg-emerald-900/40 text-emerald-300 border-emerald-700',
  pendente: 'bg-amber-900/40 text-amber-300 border-amber-700',
  atrasada: 'bg-red-900/40 text-red-300 border-red-700',
  nao_realizada: 'bg-slate-700/40 text-slate-400 border-slate-600',
};
const STATUS_LABELS = { concluida: 'Concluída', pendente: 'Pendente', atrasada: 'Atrasada', nao_realizada: 'Não Realizada' };
const FREQ_LABELS = { diario: 'Diário', semanal: 'Semanal', quinzenal: 'Quinzenal', mensal: 'Mensal', trimestral: 'Trimestral', semestral: 'Semestral', anual: 'Anual', por_horimetro: 'Horímetro' };

const PRIORITY_CONFIG = {
  critica: { color: '#F44336', bg: 'bg-red-900/60 border-red-500', label: 'CRÍTICA', icon: XCircle },
  alta: { color: '#FF9800', bg: 'bg-orange-900/60 border-orange-500', label: 'ALTA', icon: AlertTriangle },
  media: { color: '#FFC107', bg: 'bg-yellow-900/60 border-yellow-500', label: 'MÉDIA', icon: Clock },
  baixa: { color: '#2196F3', bg: 'bg-blue-900/60 border-blue-500', label: 'BAIXA', icon: CheckCircle },
};
const COR_CONFIG = {
  verde: { bg: 'bg-emerald-500', text: 'text-emerald-400', hex: '#22c55e' },
  amarelo: { bg: 'bg-yellow-500', text: 'text-yellow-400', hex: '#FFC107' },
  laranja: { bg: 'bg-orange-500', text: 'text-orange-400', hex: '#FF9800' },
  vermelho: { bg: 'bg-red-500', text: 'text-red-400', hex: '#F44336' },
};

export default function PlanoManutencaoPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('plano');
  const [tarefas, setTarefas] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [embarcacoesDB, setEmbarcacoesDB] = useState([]);
  const [notificacoes, setNotificacoes] = useState([]);
  const [horimetrosResumo, setHorimetrosResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterFreq, setFilterFreq] = useState('todos');
  const [filterEmb, setFilterEmb] = useState('todos');
  const [pagina, setPagina] = useState(1);
  const [paginacao, setPaginacao] = useState({ total: 0, total_pages: 1 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTarefa, setEditingTarefa] = useState(null);
  const [reatribuirDialog, setReatribuirDialog] = useState(null);
  const [detalheDialog, setDetalheDialog] = useState(null);
  const [leituraDialog, setLeituraDialog] = useState(null);

  const [tarefasOk, setTarefasOk] = useState(false);

  const canEdit = ['admin', 'gestor', 'operador'].includes(user?.role);
  const isAdmin = user?.role === 'admin';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(pagina), page_size: '50' });
    if (filterStatus !== 'todos') params.set('status', filterStatus);
    if (filterFreq !== 'todos') params.set('frequencia', filterFreq);
    if (filterEmb !== 'todos') params.set('embarcacao_nome', filterEmb);
    if (searchTerm.trim()) params.set('busca', searchTerm.trim());
    const results = await Promise.allSettled([
      axios.get(`${API}/manutencao-preventiva/tarefas?${params.toString()}`),
      axios.get(`${API}/manutencao-preventiva/embarcacoes-disponiveis`),
      axios.get(`${API}/manutencao-preventiva/horimetros-resumo`),
    ]);
    const [tarefasRes, embRes, horiRes] = results;
    if (tarefasRes.status === 'fulfilled') { setTarefas(tarefasRes.value.data.items); setPaginacao(tarefasRes.value.data); setTarefasOk(true); }
    if (embRes.status === 'fulfilled') setEmbarcacoesDB(embRes.value.data);
    if (horiRes.status === 'fulfilled') setHorimetrosResumo(horiRes.value.data);
    results.forEach((r, i) => { if (r.status === 'rejected') console.error(`fetchAll[${i}] falhou:`, r.reason); });
    setLoading(false);
  }, [pagina, filterStatus, filterFreq, filterEmb, searchTerm]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const embarcacoes = embarcacoesDB.map(e => e.nome);
  const fetchDashboard = async () => {
    if (dashboard) return;
    const res = await axios.get(`${API}/manutencao-preventiva/dashboard`);
    setDashboard(res.data);
  };

  const fetchNotificacoes = async () => {
    if (notificacoes.length) return;
    const res = await axios.get(`${API}/manutencao-preventiva/notificacoes`);
    setNotificacoes(res.data);
  };

  const handleTabChange = async (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'dashboard') await fetchDashboard();
    if (tabId === 'notificacoes') await fetchNotificacoes();
  };

  const handleSaveTarefa = async (data) => {
    try {
      if (editingTarefa?.id) {
        await axios.put(`${API}/manutencao-preventiva/tarefas/${editingTarefa.id}`, data);
        toast.success('Tarefa atualizada');
      } else {
        await axios.post(`${API}/manutencao-preventiva/tarefas`, data);
        toast.success('Tarefa criada');
      }
      setDialogOpen(false); setEditingTarefa(null); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
  };

  const handleAssumir = async (tarefaId) => {
    try { await axios.post(`${API}/manutencao-preventiva/tarefas/${tarefaId}/assumir`); toast.success('Tarefa assumida'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const handleReatribuir = async (tarefaId, dados) => {
    try { await axios.post(`${API}/manutencao-preventiva/tarefas/${tarefaId}/reatribuir`, dados); toast.success('Tarefa reatribuída'); setReatribuirDialog(null); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro na reatribuição'); }
  };

  const handleLeitura = async (horimetroId, valor) => {
    try { await axios.post(`${API}/manutencao-preventiva/horimetros/${horimetroId}/leitura`, { valor: parseInt(valor) }); toast.success('Leitura registrada'); setLeituraDialog(null); fetchAll(); }
    catch (e) { toast.error('Erro ao registrar leitura'); }
  };

  const handleReset = async (horimetroId) => {
    if (!window.confirm('Resetar horímetro após manutenção?')) return;
    try { await axios.post(`${API}/manutencao-preventiva/horimetros/${horimetroId}/reset`); toast.success('Horímetro resetado'); fetchAll(); }
    catch (e) { toast.error('Erro ao resetar'); }
  };

  const tabs = [
    { id: 'plano', label: 'Plano de Manutenção', icon: ClipboardList },
    { id: 'notificacoes', label: `Notificações (${notificacoes.length})`, icon: Bell },
    { id: 'acumuladores', label: 'Acumuladores', icon: Gauge },
    { id: 'relatorios', label: 'Relatórios', icon: FileText },
    { id: 'dashboard', label: 'Dashboard Gestor', icon: BarChart3 },
  ];

  const notifCriticas = notificacoes.filter(n => n.prioridade === 'critica').length;
  const notifAltas = notificacoes.filter(n => n.prioridade === 'alta').length;

  if (loading) return <div className="text-slate-400 p-8">Carregando...</div>;

  return (
    <div data-testid="plano-manutencao-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-cyan-400" /> Plano de Manutenção Preventiva
        </h1>
        <div className="flex gap-2">
          {horimetrosResumo?.criticos > 0 && <span className="px-2 py-1 rounded text-xs font-bold bg-red-600 text-white">{horimetrosResumo.criticos} Horímetro(s) Crítico(s)</span>}
          {notifCriticas > 0 && <span className="px-2 py-1 rounded text-xs font-bold bg-red-600 text-white animate-pulse">{notifCriticas} Crítica(s)</span>}
          {notifAltas > 0 && <span className="px-2 py-1 rounded text-xs font-bold bg-orange-600 text-white">{notifAltas} Alta(s)</span>}
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-700 pb-1 overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-t text-sm font-medium transition whitespace-nowrap ${activeTab === tab.id ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* TAB: Plano */}
      {activeTab === 'plano' && <TabPlano tarefas={tarefas} totalTarefas={paginacao.total} totalPaginas={paginacao.total_pages} pagina={pagina} setPagina={setPagina} tarefasOk={tarefasOk} searchInput={searchInput} setSearchInput={setSearchInput} onBuscar={() => { setPagina(1); setSearchTerm(searchInput); }} filterStatus={filterStatus} setFilterStatus={(valor) => { setPagina(1); setFilterStatus(valor); }} filterFreq={filterFreq} setFilterFreq={(valor) => { setPagina(1); setFilterFreq(valor); }} filterEmb={filterEmb} setFilterEmb={(valor) => { setPagina(1); setFilterEmb(valor); }} embarcacoes={embarcacoes} canEdit={canEdit} isAdmin={isAdmin} setEditingTarefa={setEditingTarefa} setDialogOpen={setDialogOpen} fetchAll={fetchAll} />}

      {/* TAB: Notificações */}
      {activeTab === 'notificacoes' && <TabNotificacoes notificacoes={notificacoes} embarcacoes={embarcacoes} canEdit={canEdit} handleAssumir={handleAssumir} setReatribuirDialog={setReatribuirDialog} setDetalheDialog={setDetalheDialog} />}

      {/* TAB: Acumuladores */}
      {activeTab === 'acumuladores' && <TabAcumuladores resumo={horimetrosResumo} embarcacoes={embarcacoes} canEdit={canEdit} isAdmin={isAdmin} setLeituraDialog={setLeituraDialog} handleReset={handleReset} />}

      {/* TAB: Relatórios */}
      {activeTab === 'relatorios' && <TabRelatorios embarcacoesDB={embarcacoesDB} />}

      {/* TAB: Dashboard Gestor */}
      {activeTab === 'dashboard' && dashboard && <TabDashboard dashboard={dashboard} horimetrosResumo={horimetrosResumo} isAdmin={isAdmin} fetchAll={fetchAll} />}

      {/* Dialogs */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingTarefa ? 'Editar Tarefa' : 'Nova Tarefa'}</DialogTitle></DialogHeader>
          <TarefaForm tarefa={editingTarefa} embarcacoesDB={embarcacoesDB} onSave={handleSaveTarefa} />
        </DialogContent>
      </Dialog>

      {reatribuirDialog && (
        <Dialog open={!!reatribuirDialog} onOpenChange={() => setReatribuirDialog(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
            <DialogHeader><DialogTitle>Reatribuir Tarefa</DialogTitle></DialogHeader>
            <ReatribuirForm notificacao={reatribuirDialog} onReatribuir={(d) => handleReatribuir(reatribuirDialog.tarefa_id, d)} onCancel={() => setReatribuirDialog(null)} />
          </DialogContent>
        </Dialog>
      )}

      {detalheDialog && (
        <Dialog open={!!detalheDialog} onOpenChange={() => setDetalheDialog(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
            <DialogHeader><DialogTitle>Detalhes da Tarefa</DialogTitle></DialogHeader>
            <DetalheNotificacao n={detalheDialog} />
            <DocumentUploadPanel modulo="manutencao" entidadeId={detalheDialog.id} entidadeNome={detalheDialog.servico} compact />
          </DialogContent>
        </Dialog>
      )}

      {leituraDialog && (
        <Dialog open={!!leituraDialog} onOpenChange={() => setLeituraDialog(null)}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-sm">
            <DialogHeader><DialogTitle>Leitura Manual - {leituraDialog.equipamento}</DialogTitle></DialogHeader>
            <LeituraForm horimetro={leituraDialog} onSave={handleLeitura} onCancel={() => setLeituraDialog(null)} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ===== TAB: PLANO ===== */
function TabPlano({ tarefas, totalTarefas, totalPaginas, pagina, setPagina, tarefasOk, searchInput, setSearchInput, onBuscar, filterStatus, setFilterStatus, filterFreq, setFilterFreq, filterEmb, setFilterEmb, embarcacoes, canEdit, isAdmin, setEditingTarefa, setDialogOpen, fetchAll }) {
  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-2 top-2 w-4 h-4 text-slate-500" />
          <Input value={searchInput} onChange={e => setSearchInput(e.target.value)} data-testid="input-busca-plano" placeholder="Buscar serviço, equipamento..." className="pl-8 bg-slate-800 border-slate-700 text-white h-8 text-sm" />
        </div>
        <Button type="button" data-testid="botao-buscar-plano" size="sm" className="h-8 bg-cyan-600 hover:bg-cyan-700" onClick={onBuscar}>
          <Search className="w-4 h-4 mr-1" />Buscar
        </Button>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px] bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos Status</SelectItem>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterFreq} onValueChange={setFilterFreq}>
          <SelectTrigger className="w-[130px] bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todas Freq.</SelectItem>{Object.entries(FREQ_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterEmb} onValueChange={setFilterEmb}>
          <SelectTrigger className="w-[170px] bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todas Embarcações</SelectItem>{embarcacoes.map((e, i) => <SelectItem key={`e-${i}`} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
        {canEdit && (
          <Button data-testid="btn-nova-tarefa" size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => { setEditingTarefa(null); setDialogOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" />Nova Tarefa
          </Button>
        )}
        {isAdmin && tarefasOk && totalTarefas === 0 && (
          <Button data-testid="btn-popular-plano" size="sm" variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10" onClick={async () => {
            if (!window.confirm('Popular o plano preventivo com 3 tarefas por frequência para cada embarcação ativa?')) return;
            try {
              const r = await axios.post(`${API}/manutencao-preventiva/seed-plano-completo`);
              if (r.data.erro) { toast.error(r.data.erro); return; }
              toast.success(`${r.data.inseridas} tarefas criadas para ${r.data.embarcacoes} embarcações (total: ${r.data.total_tarefas})`);
              fetchAll();
            } catch { toast.error('Erro ao popular plano'); }
          }}>
            <RotateCcw className="w-4 h-4 mr-1" />Popular Plano
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500">{tarefas.length} de {totalTarefas} tarefas</p>
      <div className="overflow-x-auto rounded border border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/80 text-slate-300">
            <tr>
              <th className="px-3 py-2 text-left">Embarcação</th><th className="px-3 py-2 text-left">Sistema</th><th className="px-3 py-2 text-left">Serviço</th>
              <th className="px-3 py-2 text-left">Equipamento</th><th className="px-3 py-2 text-center">Freq.</th><th className="px-3 py-2 text-left">Executor</th>
              <th className="px-3 py-2 text-center">Prevista</th><th className="px-3 py-2 text-center">Execução</th><th className="px-3 py-2 text-center">Status</th><th className="px-3 py-2 text-center">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {tarefas.map(t => {
              const prio = PRIORITY_CONFIG[calcPrioridade(t)] || {};
              return (
                <tr key={t.id} className="hover:bg-slate-800/40 text-slate-300">
                  <td className="px-3 py-1.5 text-white font-medium text-xs">{t.embarcacao_nome}</td>
                  <td className="px-3 py-1.5 text-xs">{t.sistema}</td><td className="px-3 py-1.5 text-xs">{t.servico}</td>
                  <td className="px-3 py-1.5 text-xs">{t.equipamento}</td>
                  <td className="px-3 py-1.5 text-center"><span className="px-1.5 py-0.5 rounded bg-slate-700 text-xs">{FREQ_LABELS[t.frequencia] || t.frequencia}</span></td>
                  <td className="px-3 py-1.5 text-xs">{t.executor_nome || <span className="text-slate-500 italic">N/A</span>}</td>
                  <td className="px-3 py-1.5 text-center text-xs">{fmtDate(t.data_prevista)}</td>
                  <td className="px-3 py-1.5 text-center text-xs">{t.data_execucao ? fmtDate(t.data_execucao) : '-'}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[t.status] || ''}`} style={t.status !== 'concluida' && prio.color ? { borderLeftColor: prio.color, borderLeftWidth: '3px' } : {}}>
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <div className="flex gap-1 justify-center">
                      {canEdit && <button title="Editar" onClick={() => { setEditingTarefa(t); setDialogOpen(true); }} className="text-cyan-400 hover:text-cyan-300"><Edit2 className="w-3.5 h-3.5" /></button>}
                      {canEdit && t.status !== 'concluida' && <button title="Concluir" onClick={async () => { await axios.put(`${API}/manutencao-preventiva/tarefas/${t.id}`, { status: 'concluida' }); toast.success('Concluída'); fetchAll(); }} className="text-emerald-400 hover:text-emerald-300"><CheckCircle className="w-3.5 h-3.5" /></button>}
                      {isAdmin && <button title="Excluir" onClick={async () => { if (window.confirm('Excluir?')) { await axios.delete(`${API}/manutencao-preventiva/tarefas/${t.id}`); fetchAll(); }}} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPaginas > 1 && (
        <div className="flex items-center justify-center gap-3 text-xs text-slate-400">
          <Button data-testid="pagina-anterior-plano" size="sm" variant="outline" disabled={pagina === 1} onClick={() => setPagina(pagina - 1)}>Anterior</Button>
          <span data-testid="status-paginacao-plano">Página {pagina} de {totalPaginas}</span>
          <Button data-testid="proxima-pagina-plano" size="sm" variant="outline" disabled={pagina === totalPaginas} onClick={() => setPagina(pagina + 1)}>Próxima</Button>
        </div>
      )}
    </div>
  );
}

/* ===== TAB: NOTIFICAÇÕES ===== */
function TabNotificacoes({ notificacoes, embarcacoes, canEdit, handleAssumir, setReatribuirDialog, setDetalheDialog }) {
  const [filterEmb, setFilterEmb] = React.useState('todos');
  const filtered = filterEmb === 'todos' ? notificacoes : notificacoes.filter(n => n.embarcacao_nome === filterEmb);
  const embsComNotif = [...new Set(notificacoes.map(n => n.embarcacao_nome).filter(Boolean))].sort();

  return (
    <div className="space-y-3">
      {/* Filtro por Embarcação */}
      <div className="flex items-center gap-3 bg-slate-800/40 rounded border border-slate-700 p-3">
        <Search className="w-4 h-4 text-slate-400" />
        <label className="text-xs text-slate-400 whitespace-nowrap">Embarcacao:</label>
        <select data-testid="notif-filter-embarcacao" value={filterEmb} onChange={e => setFilterEmb(e.target.value)}
          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-white flex-1 max-w-xs">
          <option value="todos">Todas Embarcacoes ({notificacoes.length})</option>
          {embsComNotif.map(e => {
            const count = notificacoes.filter(n => n.embarcacao_nome === e).length;
            return <option key={e} value={e}>{e} ({count})</option>;
          })}
        </select>
        {filterEmb !== 'todos' && <button onClick={() => setFilterEmb('todos')} className="text-xs text-cyan-400 hover:text-cyan-300">Limpar filtro</button>}
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} notificacao(oes)</span>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {['critica', 'alta', 'media', 'baixa'].map(p => {
          const cfg = PRIORITY_CONFIG[p]; const count = filtered.filter(n => n.prioridade === p).length;
          return (
            <div key={p} className={`rounded border p-3 ${cfg.bg}`}>
              <div className="flex items-center gap-2"><cfg.icon className="w-5 h-5" style={{ color: cfg.color }} /><span className="text-lg font-bold text-white">{count}</span></div>
              <p className="text-xs mt-1" style={{ color: cfg.color }}>{cfg.label}</p>
            </div>
          );
        })}
      </div>
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {filtered.map(n => {
          const cfg = PRIORITY_CONFIG[n.prioridade] || PRIORITY_CONFIG.media;
          return (
            <div key={n.tarefa_id} data-testid={`notif-${n.tarefa_id}`} className={`rounded border p-3 ${cfg.bg}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: cfg.color }}>{cfg.label}</span>
                  <span className="text-white font-medium text-sm">{n.servico}</span>
                </div>
                <span className="text-xs text-slate-400">{n.descricao}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-slate-300">
                <div>Equipamento: <span className="text-white">{n.equipamento} - {n.embarcacao_nome}</span></div>
                <div>Vencimento: <span className="text-white">{fmtDate(n.data_prevista)}</span></div>
                <div>Responsável: <span className="text-white">{n.executor_nome || 'Não atribuído'}</span></div>
              </div>
              {n.assumido_por && <p className="text-xs text-emerald-400 mt-1">Assumido por: {n.assumido_por}</p>}
              {n.reatribuicoes?.length > 0 && <p className="text-xs text-amber-400 mt-1">Reatribuições: {n.reatribuicoes.length}/3</p>}
              <div className="flex gap-2 mt-2">
                {!n.assumido_por && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={() => handleAssumir(n.tarefa_id)}><UserCheck className="w-3 h-3 mr-1" />Assumir</Button>}
                {canEdit && (n.reatribuicoes?.length || 0) < 3 && <Button size="sm" variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-900/30 h-7 text-xs" onClick={() => setReatribuirDialog(n)}><ArrowRightLeft className="w-3 h-3 mr-1" />Reatribuir</Button>}
                <Button size="sm" variant="outline" className="border-slate-500 text-slate-300 hover:bg-slate-700 h-7 text-xs" onClick={() => setDetalheDialog(n)}><Eye className="w-3 h-3 mr-1" />Detalhes</Button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-slate-500 text-center py-8">{filterEmb !== 'todos' ? `Nenhuma notificacao para ${filterEmb}` : 'Nenhuma notificacao pendente'}</p>}
      </div>
    </div>
  );
}

/* ===== TAB: ACUMULADORES ===== */
function TabAcumuladores({ resumo, embarcacoes, canEdit, isAdmin, setLeituraDialog, handleReset }) {
  const [filterEmb, setFilterEmb] = React.useState('todos');
  if (!resumo) return <p className="text-slate-400 p-4">Sem dados de horimetros</p>;

  const embsFiltradas = filterEmb === 'todos' ? resumo.embarcacoes : resumo.embarcacoes?.filter(e => e.embarcacao_nome === filterEmb);
  const embsComHori = (resumo.embarcacoes || []).map(e => e.embarcacao_nome).sort();

  return (
    <div className="space-y-4">
      {/* Filtro por Embarcação */}
      <div className="flex items-center gap-3 bg-slate-800/40 rounded border border-slate-700 p-3">
        <Search className="w-4 h-4 text-slate-400" />
        <label className="text-xs text-slate-400 whitespace-nowrap">Embarcacao:</label>
        <select data-testid="acum-filter-embarcacao" value={filterEmb} onChange={e => setFilterEmb(e.target.value)}
          className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-white flex-1 max-w-xs">
          <option value="todos">Todas Embarcacoes ({resumo.embarcacoes?.length || 0})</option>
          {embsComHori.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        {filterEmb !== 'todos' && <button onClick={() => setFilterEmb('todos')} className="text-xs text-cyan-400 hover:text-cyan-300">Limpar filtro</button>}
        <span className="text-xs text-slate-500 ml-auto">{embsFiltradas?.length || 0} embarcacao(oes)</span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-5 gap-3">
        <div className="bg-slate-800/50 rounded border border-slate-700 p-3"><p className="text-xs text-slate-400">Total Horímetros</p><p className="text-2xl font-bold text-white">{resumo.total}</p></div>
        <div className="bg-slate-800/50 rounded border border-emerald-700 p-3"><p className="text-xs text-emerald-400">OK (&lt;90%)</p><p className="text-2xl font-bold text-emerald-400">{resumo.ok}</p></div>
        <div className="bg-slate-800/50 rounded border border-yellow-700 p-3"><p className="text-xs text-yellow-400">Alerta (90-100%)</p><p className="text-2xl font-bold text-yellow-400">{resumo.em_alerta}</p></div>
        <div className="bg-slate-800/50 rounded border border-red-700 p-3"><p className="text-xs text-red-400">Críticos (&gt;100%)</p><p className="text-2xl font-bold text-red-400">{resumo.criticos}</p></div>
        <div className="bg-slate-800/50 rounded border border-slate-700 p-3"><p className="text-xs text-slate-400">Atraso Médio</p><p className="text-2xl font-bold text-orange-400">{resumo.atraso_medio_horas}h</p></div>
      </div>

      {/* Por Embarcação */}
      <div className="space-y-3">
        {embsFiltradas?.map(emb => (
          <div key={emb.embarcacao_id} className="bg-slate-800/30 rounded border border-slate-700 p-4">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Gauge className="w-4 h-4 text-cyan-400" />
              {emb.embarcacao_nome}
              {emb.equipamentos.some(e => e.status_cor === 'vermelho') && <span className="px-1.5 py-0.5 text-xs rounded bg-red-600 text-white">CRÍTICO</span>}
              {emb.equipamentos.some(e => e.status_cor === 'laranja') && <span className="px-1.5 py-0.5 text-xs rounded bg-orange-600 text-white">ALERTA</span>}
            </h3>
            <div className="space-y-2">
              {emb.equipamentos.map(eq => {
                const cor = COR_CONFIG[eq.status_cor] || COR_CONFIG.verde;
                return (
                  <div key={eq.id} className="flex items-center gap-3">
                    <span className="text-xs text-slate-300 w-32 shrink-0">{eq.equipamento}:</span>
                    <div className="flex-1 flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-24 text-right">{eq.horimetro_atual}h / {eq.horimetro_limite}h</span>
                      <div className="flex-1 bg-slate-700 rounded-full h-5 overflow-hidden relative">
                        <div className={`${cor.bg} h-full rounded-full transition-all`} style={{ width: `${Math.min(100, eq.percentual)}%` }} />
                        <span className="absolute inset-0 flex items-center justify-center text-xs text-white font-bold">{eq.percentual}%</span>
                      </div>
                      <span className={`text-xs font-bold w-14 text-right ${cor.text}`}>{eq.status_cor === 'verde' ? 'OK' : eq.status_cor.toUpperCase()}</span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {canEdit && <button title="Leitura Manual" onClick={() => setLeituraDialog(eq)} className="text-cyan-400 hover:text-cyan-300"><Edit2 className="w-3.5 h-3.5" /></button>}
                      {isAdmin && eq.percentual >= 100 && <button title="Resetar" onClick={() => handleReset(eq.id)} className="text-amber-400 hover:text-amber-300"><RotateCcw className="w-3.5 h-3.5" /></button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ===== TAB: RELATÓRIOS ===== */
const RELATORIOS = [
  { id: 'atrasos', nome: 'Relatorio de Atrasos', desc: 'Tarefas vencidas, proximas 48h e resumo por embarcacao', prioridade: 'alta', icon: AlertTriangle },
  { id: 'horimetros', nome: 'Relatorio de Horimetros', desc: 'Situacao de todos os acumuladores e previsoes de manutencao', prioridade: 'alta', icon: Gauge },
  { id: 'execucao', nome: 'Relatorio de Execucao', desc: 'Cumprimento de tarefas: no prazo, com atraso e nao realizadas', prioridade: 'media', icon: ClipboardList },
  { id: 'performance-embarcacoes', nome: 'Performance de Embarcacoes', desc: 'Ranking e indicadores comparativos de toda a frota', prioridade: 'media', icon: BarChart3 },
  { id: 'performance-tripulantes', nome: 'Performance de Tripulantes', desc: 'Avaliacao individual: pontuacao, cumprimento e tempo de resposta', prioridade: 'baixa', icon: UserCheck },
  { id: 'resumo-executivo', nome: 'Resumo Executivo', desc: 'Visao geral para Diretoria: indicadores, top embarcacoes e alertas', prioridade: 'baixa', icon: FileText },
];

const PRIO_BADGE = {
  alta: 'bg-red-500/20 text-red-400 border-red-500/30',
  media: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  baixa: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

function TabRelatorios({ embarcacoesDB }) {
  const { token } = useAuth();
  const [gerando, setGerando] = useState(null);
  const [textoResult, setTextoResult] = useState(null);
  const [embFiltro, setEmbFiltro] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const gerarRelatorio = async (tipo, formato) => {
    setGerando(`${tipo}-${formato}`);
    setTextoResult(null);
    try {
      const params = new URLSearchParams({ formato });
      if (embFiltro) params.set('embarcacao_id', embFiltro);
      if (dataInicio) params.set('data_inicio', dataInicio);
      if (dataFim) params.set('data_fim', dataFim);
      const url = `${API}/manutencao-preventiva/relatorios/${tipo}?${params.toString()}`;
      const embNome = embFiltro ? (embarcacoesDB.find(e => e.id === embFiltro)?.nome || '').replace(/\s+/g, '_') : '';
      if (formato === 'texto') {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setTextoResult({ tipo, conteudo: data.relatorio });
      } else {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('Erro ao gerar');
        const blob = await res.blob();
        const ext = formato === 'pdf' ? 'pdf' : 'xlsx';
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `relatorio_${tipo}${embNome ? '_' + embNome : ''}_${new Date().toISOString().slice(0, 10)}.${ext}`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success(`Relatorio ${formato.toUpperCase()} baixado`);
      }
    } catch (e) {
      toast.error('Erro ao gerar relatorio');
    } finally {
      setGerando(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="tab-relatorios">
      <div className="bg-slate-800/30 rounded border border-slate-700 p-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-white mb-1">Gerar Relatorios de Manutencao</h3>
          <p className="text-xs text-slate-500">Selecione o tipo de relatorio e o formato de saida desejado.</p>
        </div>
        <div className="min-w-[260px]">
          <label className="text-xs text-slate-400 block mb-1">Embarcação</label>
          <select
            data-testid="select-embarcacao-relatorio"
            value={embFiltro}
            onChange={e => setEmbFiltro(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white"
          >
            <option value="">Todas as Embarcações</option>
            {embarcacoesDB.map(e => (
              <option key={e.id} value={e.id}>{e.nome} ({e.tipo})</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor="relatorio-data-inicio" className="text-xs text-slate-400 block mb-1">Data início</label>
            <Input id="relatorio-data-inicio" type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} data-testid="input-data-inicio-relatorio" className="h-9 bg-slate-800 border-slate-700 text-white" />
          </div>
          <div>
            <label htmlFor="relatorio-data-fim" className="text-xs text-slate-400 block mb-1">Data fim</label>
            <Input id="relatorio-data-fim" type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} data-testid="input-data-fim-relatorio" className="h-9 bg-slate-800 border-slate-700 text-white" />
          </div>
        </div>
      </div>

      <div className="grid gap-3">
        {RELATORIOS.map(rel => {
          const Icon = rel.icon;
          return (
            <div key={rel.id} data-testid={`relatorio-${rel.id}`} className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="p-2 rounded bg-slate-800 shrink-0"><Icon className="w-5 h-5 text-cyan-400" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-white">{rel.nome}</h4>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase ${PRIO_BADGE[rel.prioridade]}`}>{rel.prioridade}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{rel.desc}</p>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => gerarRelatorio(rel.id, 'pdf')} disabled={!!gerando} data-testid={`btn-pdf-${rel.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/40 border border-red-500/30 text-red-300 rounded text-xs font-medium transition disabled:opacity-40">
                    {gerando === `${rel.id}-pdf` ? <span className="animate-spin">...</span> : <Download className="w-3 h-3" />} PDF
                  </button>
                  <button onClick={() => gerarRelatorio(rel.id, 'excel')} disabled={!!gerando} data-testid={`btn-excel-${rel.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-500/30 text-emerald-300 rounded text-xs font-medium transition disabled:opacity-40">
                    {gerando === `${rel.id}-excel` ? <span className="animate-spin">...</span> : <Download className="w-3 h-3" />} Excel
                  </button>
                  <button onClick={() => gerarRelatorio(rel.id, 'texto')} disabled={!!gerando} data-testid={`btn-texto-${rel.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-cyan-600/20 hover:bg-cyan-600/40 border border-cyan-500/30 text-cyan-300 rounded text-xs font-medium transition disabled:opacity-40">
                    {gerando === `${rel.id}-texto` ? <span className="animate-spin">...</span> : <Eye className="w-3 h-3" />} Texto
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {textoResult && (
        <div className="bg-slate-900/80 border border-cyan-500/30 rounded-lg overflow-hidden" data-testid="texto-result">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700 bg-slate-800/50">
            <h4 className="text-sm font-semibold text-cyan-400">Resultado: {RELATORIOS.find(r => r.id === textoResult.tipo)?.nome}</h4>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard.writeText(textoResult.conteudo); toast.success('Copiado!'); }}
                className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 transition">Copiar</button>
              <button onClick={() => setTextoResult(null)} className="text-xs text-slate-500 hover:text-white px-2 py-1">Fechar</button>
            </div>
          </div>
          <pre className="p-4 text-xs text-slate-300 font-mono whitespace-pre-wrap overflow-x-auto max-h-[50vh] overflow-y-auto">{textoResult.conteudo}</pre>
        </div>
      )}
    </div>
  );
}


/* ===== TAB: DASHBOARD GESTOR ===== */
function TabDashboard({ dashboard, horimetrosResumo, isAdmin, fetchAll }) {
  const { token } = useAuth();
  const [osStatus, setOsStatus] = useState(null);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    fetch(`${API}/manutencao-preventiva/os-auto-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setOsStatus).catch(() => {});
  }, [token]);

  const handleGerarOS = async () => {
    setGerando(true);
    try {
      const res = await fetch(`${API}/manutencao-preventiva/gerar-os-automatica`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      toast.success(data.message || 'OS geradas');
      setOsStatus(prev => ({ ...prev, ultima_execucao: data, total_os_automaticas: (prev?.total_os_automaticas || 0) + data.tarefas_geradas }));
      if (fetchAll) fetchAll();
    } catch { toast.error('Erro ao gerar OS'); }
    finally { setGerando(false); }
  };

  const ind = dashboard.indicadores;
  return (
    <div className="space-y-4">
      {/* Geração Automática de OS */}
      <div className="bg-slate-800/30 rounded border border-cyan-700/40 p-4" data-testid="os-auto-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Clock className="w-4 h-4 text-cyan-400" /> Geracao Automatica de Ordens de Servico
            {osStatus?.scheduler_ativo && <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-600/30 text-emerald-400 border border-emerald-600/40">ATIVO</span>}
          </h3>
          {isAdmin && (
            <Button size="sm" onClick={handleGerarOS} disabled={gerando} data-testid="btn-gerar-os"
              className="bg-cyan-600 hover:bg-cyan-500 h-7 text-xs">
              {gerando ? 'Gerando...' : 'Gerar OS Agora'}
            </Button>
          )}
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-slate-900/50 rounded border border-slate-700 p-2.5">
            <p className="text-[10px] text-slate-500 uppercase">Proxima Execucao</p>
            <p className="text-xs text-white font-medium mt-0.5">Diario 00:00 (Brasilia)</p>
          </div>
          <div className="bg-slate-900/50 rounded border border-slate-700 p-2.5">
            <p className="text-[10px] text-slate-500 uppercase">Ultima Execucao</p>
            <p className="text-xs text-white font-medium mt-0.5">{osStatus?.ultima_execucao ? new Date(osStatus.ultima_execucao.data_execucao).toLocaleString('pt-BR') : 'Nunca'}</p>
          </div>
          <div className="bg-slate-900/50 rounded border border-emerald-700/40 p-2.5">
            <p className="text-[10px] text-emerald-500 uppercase">OS Geradas (Ultima)</p>
            <p className="text-sm text-emerald-400 font-bold mt-0.5">{osStatus?.ultima_execucao?.tarefas_geradas ?? '-'}</p>
          </div>
          <div className="bg-slate-900/50 rounded border border-slate-700 p-2.5">
            <p className="text-[10px] text-slate-500 uppercase">Total OS Automaticas</p>
            <p className="text-sm text-cyan-400 font-bold mt-0.5">{osStatus?.total_os_automaticas ?? 0}</p>
          </div>
        </div>
        {osStatus?.ultima_execucao?.detalhes?.length > 0 && (
          <details className="mt-3">
            <summary className="text-xs text-slate-400 cursor-pointer hover:text-white">Ver ultimas OS geradas ({osStatus.ultima_execucao.tarefas_geradas})</summary>
            <div className="mt-2 max-h-40 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500"><tr><th className="px-2 py-1 text-left">Servico</th><th className="px-2 py-1 text-left">Embarcacao</th><th className="px-2 py-1 text-center">Freq.</th><th className="px-2 py-1 text-center">Data Prevista</th></tr></thead>
                <tbody className="divide-y divide-slate-800/50">
                  {osStatus.ultima_execucao.detalhes.map((d, i) => (
                    <tr key={i} className="text-slate-300"><td className="px-2 py-1">{d.servico}</td><td className="px-2 py-1">{d.embarcacao}</td><td className="px-2 py-1 text-center">{d.frequencia}</td><td className="px-2 py-1 text-center">{d.data_prevista}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </div>

      {/* Indicadores Gerais da Frota */}
      <div className="bg-slate-800/30 rounded border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Indicadores Gerais da Frota (Últimos 30 dias)</h3>
        <div className="grid grid-cols-4 gap-3">
          <IndicadorCard label="Disponibilidade" value={`${ind.disponibilidade_frota}%`} meta={`Meta: ${ind.meta_disponibilidade}%`} ok={ind.disponibilidade_frota >= ind.meta_disponibilidade} />
          <IndicadorCard label="Tarefas Realizadas" value={ind.total_concluidas} meta={`Total: ${ind.total_tarefas}`} />
          <IndicadorCard label="% Cumprimento" value={`${ind.percentual_geral}%`} meta={`Meta: ${ind.meta_cumprimento}%`} ok={ind.percentual_geral >= ind.meta_cumprimento} />
          <IndicadorCard label="Atrasadas >7d" value={ind.atrasadas_7d} meta="Meta: 0" ok={ind.atrasadas_7d === 0} alert={ind.atrasadas_7d > 0} />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <IndicadorCard label="Total Atrasadas" value={ind.total_atrasadas} meta={`${ind.total_atrasadas < 10 ? 'OK' : 'ALERTA'}`} ok={ind.total_atrasadas < 10} />
          <IndicadorCard label="No Prazo" value={ind.total_no_prazo} />
          <IndicadorCard label="Média/Tripulante" value={ind.media_tarefas_por_tripulante} />
        </div>
      </div>

      {/* Horímetros Summary */}
      {horimetrosResumo && (
        <div className="bg-slate-800/30 rounded border border-slate-700 p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Indicadores de Horímetro</h3>
          <div className="grid grid-cols-4 gap-3">
            <IndicadorCard label="Horímetros em Alerta" value={`${horimetrosResumo.em_alerta}`} meta={`< 20% da frota`} ok={horimetrosResumo.em_alerta < horimetrosResumo.total * 0.2} />
            <IndicadorCard label="Horímetros Críticos" value={horimetrosResumo.criticos} meta="Meta: 0" ok={horimetrosResumo.criticos === 0} alert={horimetrosResumo.criticos > 0} />
            <IndicadorCard label="Atraso Médio (h)" value={`${horimetrosResumo.atraso_medio_horas}h`} meta="Meta: < 10h" ok={horimetrosResumo.atraso_medio_horas < 10} />
            <IndicadorCard label="Precisão Automática" value={`${horimetrosResumo.precisao_automatica}%`} meta="Meta: > 98%" ok={horimetrosResumo.precisao_automatica >= 98} />
          </div>
        </div>
      )}

      {/* Atrasos por Equipamento */}
      {dashboard.atrasos_equipamento?.length > 0 && (
        <div className="bg-slate-800/30 rounded border border-slate-700 p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Tarefas Atrasadas por Equipamento</h3>
          <table className="w-full text-sm">
            <thead className="text-slate-400 text-xs">
              <tr><th className="px-3 py-1.5 text-left">Equipamento/Sistema</th><th className="px-3 py-1.5 text-center">Atrasadas</th><th className="px-3 py-1.5 text-center">Críticas (&gt;7d)</th><th className="px-3 py-1.5 text-left">Ação</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {dashboard.atrasos_equipamento.map(a => (
                <tr key={a.equipamento} className="text-slate-300">
                  <td className="px-3 py-1.5 text-white text-xs">{a.equipamento}</td>
                  <td className="px-3 py-1.5 text-center text-red-400 font-bold text-xs">{a.atrasadas}</td>
                  <td className="px-3 py-1.5 text-center text-xs">{a.criticas_7d > 0 ? <span className="text-red-400 font-bold">{a.criticas_7d}</span> : '0'}</td>
                  <td className="px-3 py-1.5 text-xs">{a.criticas_7d > 0 ? <span className="text-red-400">URGENTE</span> : 'Normal'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top 5 atrasados */}
      {ind.top5_atrasados?.length > 0 && (
        <div className="bg-slate-800/30 rounded border border-slate-700 p-4">
          <h3 className="text-sm font-semibold text-white mb-2">Top 5 Serviços Mais Atrasados</h3>
          {ind.top5_atrasados.map((s, i) => (
            <div key={i} className="flex items-center gap-2 mb-1">
              <span className="text-red-400 font-mono text-xs w-6">{s.count}x</span>
              <div className="flex-1 bg-slate-700/50 rounded h-5 overflow-hidden">
                <div className="bg-red-600/60 h-full rounded flex items-center px-2" style={{ width: `${Math.min(100, s.count * 15)}%` }}>
                  <span className="text-xs text-white truncate">{s.servico}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ranking Tripulantes */}
      {dashboard.performance_tripulantes?.length > 0 && (
        <div className="bg-slate-800/30 rounded border border-slate-700 p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Ranking de Performance dos Tripulantes</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-400 text-xs">
                <tr>
                  <th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Nome</th><th className="px-2 py-1 text-left">Cargo</th>
                  <th className="px-2 py-1 text-center">Tarefas</th><th className="px-2 py-1 text-center">No Prazo</th><th className="px-2 py-1 text-center">Atrasadas</th>
                  <th className="px-2 py-1 text-center">Reatrib.</th><th className="px-2 py-1 text-center">Cumprimento</th>
                  <th className="px-2 py-1 text-center">Pontuação</th><th className="px-2 py-1 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {dashboard.performance_tripulantes.map((p, i) => {
                  const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}º`;
                  const statusColor = p.status_geral === 'Excelente' ? 'text-emerald-400' : p.status_geral === 'Bom' ? 'text-cyan-400' : p.status_geral === 'Regular' ? 'text-amber-400' : 'text-red-400';
                  return (
                    <tr key={p.login} className="text-slate-300 hover:bg-slate-800/40">
                      <td className="px-2 py-1 text-xs">{medal}</td>
                      <td className="px-2 py-1 text-white text-xs">{p.nome}</td>
                      <td className="px-2 py-1 text-xs">{p.cargo}</td>
                      <td className="px-2 py-1 text-center text-xs">{p.total_tarefas}</td>
                      <td className="px-2 py-1 text-center text-emerald-400 text-xs">{p.executadas_no_prazo}</td>
                      <td className="px-2 py-1 text-center text-red-400 text-xs">{p.atrasadas}</td>
                      <td className="px-2 py-1 text-center text-xs">{p.reatribuicoes}</td>
                      <td className="px-2 py-1 text-center">
                        <div className="flex items-center gap-1">
                          <div className="flex-1 bg-slate-700 rounded h-3 overflow-hidden"><div className="bg-cyan-500 h-full rounded" style={{ width: `${p.percentual_cumprimento}%` }} /></div>
                          <span className="text-xs w-10 text-right">{p.percentual_cumprimento}%</span>
                        </div>
                      </td>
                      <td className="px-2 py-1 text-center">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${p.pontuacao >= 90 ? 'bg-emerald-900/50 text-emerald-300' : p.pontuacao >= 70 ? 'bg-cyan-900/50 text-cyan-300' : p.pontuacao >= 50 ? 'bg-amber-900/50 text-amber-300' : 'bg-red-900/50 text-red-300'}`}>
                          {p.pontuacao}
                        </span>
                      </td>
                      <td className={`px-2 py-1 text-center text-xs font-bold ${statusColor}`}>{p.status_geral}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Alertas > 7 dias */}
      {dashboard.alertas?.length > 0 && (
        <div className="bg-red-950/30 rounded border border-red-800 p-4">
          <h3 className="text-sm font-semibold text-red-300 mb-2">Tarefas Atrasadas &gt; 7 dias</h3>
          <table className="w-full text-sm">
            <thead className="text-red-400 text-xs"><tr><th className="px-2 text-left">Serviço</th><th className="px-2 text-left">Embarcação</th><th className="px-2 text-left">Executor</th><th className="px-2 text-center">Prevista</th><th className="px-2 text-center">Atraso</th></tr></thead>
            <tbody className="divide-y divide-red-900/30">
              {dashboard.alertas.map(a => (
                <tr key={a.tarefa_id} className="text-slate-300"><td className="px-2 py-1 text-xs">{a.servico}</td><td className="px-2 py-1 text-xs">{a.embarcacao}</td><td className="px-2 py-1 text-xs">{a.executor || 'N/A'}</td><td className="px-2 py-1 text-center text-xs">{fmtDate(a.data_prevista)}</td><td className="px-2 py-1 text-center font-bold text-red-400 text-xs">{a.dias_atraso}d</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ===== COMPONENTS ===== */
function IndicadorCard({ label, value, meta, ok, alert }) {
  return (
    <div className={`bg-slate-800/50 rounded border p-3 ${alert ? 'border-red-600' : ok === true ? 'border-emerald-700' : ok === false ? 'border-amber-700' : 'border-slate-700'}`}>
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`text-xl font-bold ${alert ? 'text-red-400' : ok === true ? 'text-emerald-400' : ok === false ? 'text-amber-400' : 'text-white'}`}>{value}</p>
      {meta && <p className="text-xs mt-0.5">{ok === true ? <span className="text-emerald-400">&#10003; {meta}</span> : ok === false ? <span className="text-amber-400">&#9888; {meta}</span> : <span className="text-slate-500">{meta}</span>}</p>}
    </div>
  );
}

function LeituraForm({ horimetro, onSave, onCancel }) {
  const [valor, setValor] = useState(horimetro.horimetro_atual || 0);
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-400">Valor atual: <span className="text-white font-bold">{horimetro.horimetro_atual}h</span> / {horimetro.horimetro_limite}h</p>
      <div><Label className="text-slate-300 text-xs">Nova Leitura (horas)</Label><Input type="number" value={valor} onChange={e => setValor(e.target.value)} className="bg-slate-800 border-slate-700 text-white" /></div>
      <div className="flex gap-2">
        <Button className="flex-1 bg-cyan-600 hover:bg-cyan-700" onClick={() => onSave(horimetro.id, valor)}>Registrar</Button>
        <Button variant="outline" className="border-slate-600" onClick={onCancel}>Cancelar</Button>
      </div>
    </div>
  );
}

function DetalheNotificacao({ n }) {
  const cfg = PRIORITY_CONFIG[n.prioridade] || PRIORITY_CONFIG.media;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2"><span className="px-2 py-1 rounded text-xs font-bold text-white" style={{ backgroundColor: cfg.color }}>{cfg.label}</span><span className="text-white font-semibold">{n.servico}</span></div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="text-slate-400">Equipamento: <span className="text-white">{n.equipamento}</span></div>
        <div className="text-slate-400">Embarcação: <span className="text-white">{n.embarcacao_nome}</span></div>
        <div className="text-slate-400">Vencimento: <span className="text-white">{fmtDate(n.data_prevista)}</span></div>
        <div className="text-slate-400">Frequência: <span className="text-white">{FREQ_LABELS[n.frequencia] || n.frequencia}</span></div>
        <div className="text-slate-400">Responsável: <span className="text-white">{n.executor_nome || 'Não atribuído'}</span></div>
        <div className="text-slate-400">Cargo: <span className="text-white">{n.executor_cargo || '-'}</span></div>
      </div>
      {n.assumido_por && <div className="bg-emerald-900/30 rounded p-2 border border-emerald-700"><p className="text-xs text-emerald-300">Assumido por: {n.assumido_por}</p></div>}
      {n.reatribuicoes?.length > 0 && (
        <div className="bg-amber-900/20 rounded p-2 border border-amber-700">
          <p className="text-xs text-amber-300 font-semibold mb-1">Reatribuições ({n.reatribuicoes.length}/3):</p>
          {n.reatribuicoes.map((r, i) => <p key={i} className="text-xs text-slate-300">{r.de} &rarr; {r.para} (por {r.por}) {r.motivo && `- "${r.motivo}"`}</p>)}
        </div>
      )}
    </div>
  );
}

function ReatribuirForm({ notificacao, onReatribuir, onCancel }) {
  const [tripulantes, setTripulantes] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (notificacao.embarcacao_id) {
      setLoading(true);
      axios.get(`${API}/manutencao-preventiva/tripulantes-por-embarcacao/${notificacao.embarcacao_id}?sistema=${encodeURIComponent(notificacao.sistema || '')}`)
        .then(res => setTripulantes(res.data.tripulantes || []))
        .catch(() => setTripulantes([]))
        .finally(() => setLoading(false));
    }
  }, [notificacao.embarcacao_id, notificacao.sistema]);
  const selected = tripulantes.find(t => t.id === selectedTrip);
  return (
    <div className="space-y-3">
      <div className="bg-slate-800 rounded p-2 text-xs">
        <p className="text-slate-400">Tarefa: <span className="text-white">{notificacao.servico}</span></p>
        <p className="text-slate-400">Executor atual: <span className="text-white">{notificacao.executor_nome || 'Nenhum'}</span></p>
        <p className="text-slate-400">Reatribuições: <span className="text-amber-400">{notificacao.reatribuicoes?.length || 0}/3</span></p>
      </div>
      <div>
        <Label className="text-slate-300 text-xs">Novo Responsável {loading && '(carregando...)'}</Label>
        <Select value={selectedTrip} onValueChange={setSelectedTrip}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-9"><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>
            {tripulantes.map(t => (
              <SelectItem key={t.id} value={t.id} disabled={t.bloqueado}>
                {t.nome} ({t.funcao}) {t.cargo_match ? '[Rec]' : ''} {t.bloqueado ? '[BLOQ]' : ''} {t.sobrecarga === 'alerta' ? `[${t.tarefas_pendentes}p]` : ''}
              </SelectItem>
            ))}
            {tripulantes.length === 0 && !loading && <div className="px-2 py-1 text-xs text-slate-400">Nenhum tripulante</div>}
          </SelectContent>
        </Select>
        {selected?.sobrecarga === 'alerta' && <p className="text-xs text-amber-400 mt-1">Este tripulante tem {selected.tarefas_pendentes} tarefas pendentes</p>}
      </div>
      <div><Label className="text-slate-300 text-xs">Motivo (opcional)</Label><Textarea value={motivo} onChange={e => setMotivo(e.target.value)} className="bg-slate-800 border-slate-700 text-white text-sm" rows={2} /></div>
      <div className="flex gap-2">
        <Button className="flex-1 bg-amber-600 hover:bg-amber-700" disabled={!selectedTrip} onClick={() => { const t = tripulantes.find(t => t.id === selectedTrip); onReatribuir({ novo_executor_id: selectedTrip, novo_executor_nome: t?.nome, novo_executor_cargo: t?.funcao, motivo }); }}>Confirmar</Button>
        <Button variant="outline" className="border-slate-600" onClick={onCancel}>Cancelar</Button>
      </div>
    </div>
  );
}

function TarefaForm({ tarefa, embarcacoesDB, onSave }) {
  const [form, setForm] = useState(tarefa || { servico: '', sistema: '', equipamento: '', frequencia: 'mensal', frequencia_valor: null, frequencia_unidade: '', executor_login: '', data_prevista: '', status: 'pendente', observacoes: '', pontos_verificacao: '', embarcacao_nome: '', embarcacao_id: '', cargo_recomendado: '' });
  const [tripulantesEmb, setTripulantesEmb] = useState([]);
  const [fallbackPool, setFallbackPool] = useState(false);
  const [loadingTrip, setLoadingTrip] = useState(false);
  const [cargoRec, setCargoRec] = useState(tarefa?.cargo_recomendado || '');
  useEffect(() => {
    if (!form.embarcacao_id) return;
    setLoadingTrip(true);
    axios.get(`${API}/manutencao-preventiva/tripulantes-por-embarcacao/${form.embarcacao_id}?sistema=${encodeURIComponent(form.sistema || '')}`)
      .then(async r => {
        if (r.data.cargo_recomendado) setCargoRec(r.data.cargo_recomendado);
        const embarcados = r.data.tripulantes || [];
        if (embarcados.length > 0) { setTripulantesEmb(embarcados); setFallbackPool(false); }
        else {
          const all = await axios.get(`${API}/tripulantes`);
          const lista = (all.data || []).filter(t => t.id && t.nome).map(t => ({ id: t.id, nome: t.nome, funcao: t.funcao || '', cargo_match: false, bloqueado: false }));
          setTripulantesEmb(lista); setFallbackPool(true);
        }
      })
      .catch(() => setTripulantesEmb([]))
      .finally(() => setLoadingTrip(false));
  }, [form.embarcacao_id, form.sistema]);
  const handleSistemaChange = async (s) => { setForm(f => ({...f, sistema: s})); try { const r = await axios.get(`${API}/manutencao-preventiva/cargo-recomendado?sistema=${encodeURIComponent(s)}`); setCargoRec(r.data.cargo_recomendado || ''); } catch {} };
  const handleEquipChange = async (e) => { setForm(f => ({...f, equipamento: e})); try { const r = await axios.get(`${API}/manutencao-preventiva/cargo-recomendado?equipamento=${encodeURIComponent(e)}&sistema=${encodeURIComponent(form.sistema || '')}`); setCargoRec(r.data.cargo_recomendado || ''); } catch {} };
  const handleEmb = (id) => { const e = embarcacoesDB.find(x => x.id === id); setForm(f => ({...f, embarcacao_id: id, embarcacao_nome: e?.nome || '', executor_login: ''})); };
  const handleSubmit = (e) => { e.preventDefault(); if (!form.embarcacao_id) { toast.error('Selecione a embarcação'); return; } if (!form.executor_login) { toast.error('Selecione o executor'); return; } const ex = tripulantesEmb.find(t => t.id === form.executor_login); onSave({...form, executor_nome: ex?.nome || form.executor_nome || '', executor_cargo: ex?.funcao || form.executor_cargo || '', cargo_recomendado: cargoRec}); };
  const SISTEMAS = ['Motor Principal', 'Grupo Gerador', 'Guindaste', 'Sistema Hidráulico', 'Rede de Bombas', 'Reversora', 'Equipamento de Navegação'];
  const EQUIPAMENTOS = ['Motor', 'Gerador', 'Guindaste', 'Sistema Hidráulico do Leme', 'Rede de Bombas', 'Reversora'];
  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-slate-300 text-xs">Serviço *</Label><Input value={form.servico} onChange={e => setForm(f => ({...f, servico: e.target.value}))} className="bg-slate-800 border-slate-700 text-white h-8 text-sm" required /></div>
        <div><Label className="text-slate-300 text-xs">Sistema</Label><Select value={form.sistema} onValueChange={handleSistemaChange}><SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{SISTEMAS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-slate-300 text-xs">Equipamento</Label><Select value={form.equipamento} onValueChange={handleEquipChange}><SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{EQUIPAMENTOS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-slate-300 text-xs">Frequência</Label><Select value={form.frequencia} onValueChange={v => setForm(f => ({...f, frequencia: v}))}><SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(FREQ_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-slate-300 text-xs">Embarcação</Label><Select value={form.embarcacao_id} onValueChange={handleEmb}><SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue placeholder="Selecione" /></SelectTrigger><SelectContent>{embarcacoesDB.map(e => <SelectItem key={e.id} value={e.id}>{e.nome} ({e.tipo})</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-slate-300 text-xs">Executor {loadingTrip && '(...)'}{cargoRec && <span className="text-cyan-400 ml-1">[Rec: {cargoRec}]</span>}{fallbackPool && <span className="text-amber-400 ml-1">[Pool geral - sem tripulação embarcada]</span>}</Label><Select value={form.executor_login} onValueChange={v => setForm(f => ({...f, executor_login: v}))} disabled={!form.embarcacao_id}><SelectTrigger data-testid="select-executor-tarefa" className="bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue placeholder={!form.embarcacao_id ? 'Selecione embarcação' : 'Selecione'} /></SelectTrigger><SelectContent>{tripulantesEmb.map(t => <SelectItem key={t.id} value={t.id} disabled={t.bloqueado}>{t.nome} ({t.funcao}) {t.cargo_match ? '[Rec]' : ''}{t.bloqueado ? ' [BLOQ]' : ''}</SelectItem>)}{tripulantesEmb.length === 0 && form.embarcacao_id && <div className="px-2 py-1 text-xs text-slate-400">Nenhum tripulante</div>}</SelectContent></Select></div>
        <div><Label className="text-slate-300 text-xs">Data Prevista</Label><Input type="date" value={form.data_prevista} onChange={e => setForm(f => ({...f, data_prevista: e.target.value}))} className="bg-slate-800 border-slate-700 text-white h-8 text-sm" /></div>
        <div><Label className="text-slate-300 text-xs">Status</Label><Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}><SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div><Label className="text-slate-300 text-xs">Pontos de Verificação</Label><Textarea value={form.pontos_verificacao} onChange={e => setForm(f => ({...f, pontos_verificacao: e.target.value}))} className="bg-slate-800 border-slate-700 text-white text-sm" rows={2} /></div>
      <div><Label className="text-slate-300 text-xs">Observações</Label><Textarea value={form.observacoes} onChange={e => setForm(f => ({...f, observacoes: e.target.value}))} className="bg-slate-800 border-slate-700 text-white text-sm" rows={2} /></div>
      <Button type="submit" className="w-full bg-cyan-600 hover:bg-cyan-700">Salvar</Button>
    </form>
  );
}

function fmtDate(d) { if (!d) return '-'; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; }
function calcPrioridade(t) {
  if (t.status === 'concluida') return 'baixa';
  if (!t.data_prevista) return 'media';
  try { const dp = new Date(t.data_prevista + 'T00:00:00'); const diff = (dp - new Date()) / 3600000; if (diff < 0) return 'critica'; if (diff <= 48) return 'alta'; if (diff <= 168) return 'media'; return 'baixa'; } catch { return 'media'; }
}
